import { z } from 'zod';
import { schemas } from '../../generated/api-client';
import { TypedApiClient } from '../index';
import { apiPath } from '../path';

// ====== Import schemas from generated code (ATL-450) ======
// The hand-written schema declared snake_case keys (firestore_id/store_id/
// client_name/total_price/…) while the backend transformOrder returns camelCase,
// so every declared field was always undefined and only `.passthrough()` leaked
// the real values untyped. Use the generated camelCase response schema instead.
export const OrderResponseSchema = schemas.OrderFirestoreResponse;

export const UpdateOrderSchema = z.object({
  // ATL-1103: the status is the Firestore MAP ({id, …, text}) or a bare status id. Declared
  // `z.string()` here, this schema REJECTED the map before it ever left the device — a
  // hand-written schema drifting from the generated one, which is the thing the api-client
  // convention exists to prevent.
  status: z.union([z.record(z.unknown()), z.string()]).optional(),
  items: z.array(z.any()).optional(),
  totalPrice: z.number().optional(),
  deliveryAddress: z.string().optional(),
  deliveryPhone: z.string().optional(),
});

// Schema for public order item
export const PublicOrderItemSchema = z.object({
  productId: z.string(),
  quantity: z.number(),
  price: z.number(),
  name: z.string().optional(),
}).passthrough();

// Schema for creating a public order (marketplace - no auth required)
export const CreatePublicOrderSchema = z.object({
  organizationId: z.string(),
  storeId: z.string().optional(),
  clientName: z.string().optional(),
  clientPhone: z.string().optional(),
  name: z.string().optional(),
  phone: z.string().optional(),
  clientEmail: z.string().optional(),
  items: z.array(PublicOrderItemSchema),
  totalPrice: z.number().optional(),
  firestoreId: z.string().optional(),
  notes: z.string().optional(),
});

// Schema for order comparison result
export const OrderComparisonResultSchema = z.object({
  firestoreId: z.string(),
  supabaseId: z.string().optional(),
  foundInSupabase: z.boolean(),
  foundInFirestore: z.boolean(),
  matches: z.boolean(),
  differences: z.array(z.object({
    field: z.string(),
    supabaseValue: z.unknown(),
    firestoreValue: z.unknown(),
  })),
});

// Type exports
export type OrderResponse = z.infer<typeof OrderResponseSchema>;
export type UpdateOrder = z.infer<typeof UpdateOrderSchema>;
export type PublicOrderItem = z.infer<typeof PublicOrderItemSchema>;
export type CreatePublicOrder = z.infer<typeof CreatePublicOrderSchema>;
export type OrderComparisonResult = z.infer<typeof OrderComparisonResultSchema>;

// API factory function
export function createOrdersApi(client: TypedApiClient) {
  return {
    getOrders: (params?: {
      storeId?: string;
      /** A status ID — matched against the generated `status_id` column, so it matches a map too. */
      status?: string;
      startDate?: string;
      endDate?: string;
      /** ATL-986: `false` is the "active orders only" list on ClientOrdersScreen. */
      isProcessed?: boolean;
      limit?: number;
      offset?: number;
    }): Promise<OrderResponse[]> => {
      return client.get('/orders', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(OrderResponseSchema),
      });
    },

    getOrderById: (id: string): Promise<OrderResponse> => {
      return client.get(apiPath`/orders/${id}`, {
        responseSchema: OrderResponseSchema,
      });
    },

    updateOrder: (id: string, data: UpdateOrder): Promise<OrderResponse> => {
      return client.patch(apiPath`/orders/${id}`, {
        data,
        responseSchema: OrderResponseSchema,
      });
    },

    /**
     * Create a public order (no auth required) - for marketplace
     */
    createPublicOrder: (data: CreatePublicOrder): Promise<OrderResponse> => {
      return client.post('/orders/public', {
        data,
        requestSchema: CreatePublicOrderSchema,
        responseSchema: OrderResponseSchema,
      });
    },

    /**
     * Compare order between Supabase and Firestore (for dual-write testing)
     */
    compareOrder: (
      firestoreId: string,
      organizationId: string,
    ): Promise<OrderComparisonResult> => {
      return client.get(apiPath`/orders/public/compare/${firestoreId}`, {
        params: { organizationId },
        responseSchema: OrderComparisonResultSchema,
      });
    },
  };
}
